import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Grid,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  CircularProgress,
  Button,
  Tab,
  Tabs,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Receipt as ReceiptIcon,
  Token as TokenIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { billingApi } from '../../services/api/billing';
import { 
  PRICING_TIERS, 
  MEP_SPECIALIZED_PRODUCT,
  BillingFrequency,
  Subscription,
  BillingSummary,
  SpecializedProduct,
  PricingTier
} from '../../types/billing';
import { TierSelectionCard } from '../../components/Billing/TierSelectionCard';
import { SpecializedProductCard } from '../../components/Billing/SpecializedProductCard';
import { useAuth } from '../../hooks/useAuth';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`billing-tabpanel-${index}`}
      aria-labelledby={`billing-tab-${index}`}
      {...other}
    >
      {value === index && <div style={{ paddingTop: 24 }}>{children}</div>}
    </div>
  );
};

export const SubscriptionPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [billingFrequency, setBillingFrequency] = useState<BillingFrequency>(BillingFrequency.MONTHLY);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [billingSummary, setBillingSummary] = useState<BillingSummary | null>(null);
  const [specializedProducts] = useState<SpecializedProduct[]>([MEP_SPECIALIZED_PRODUCT]);
  const [updatingSubscription, setUpdatingSubscription] = useState(false);

  const fetchBillingData = useCallback(async () => {
    if (!user?.organizationId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const [subscriptionData, summaryData] = await Promise.all([
        billingApi.getSubscription(user.organizationId),
        billingApi.getBillingSummary(user.organizationId),
      ]);
      
      setSubscription(subscriptionData);
      setBillingSummary(summaryData);
      setBillingFrequency(subscriptionData.billingFrequency);
    } catch (err) {
      console.error('Error fetching billing data:', err);
      setError('Failed to load billing information. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user?.organizationId]);

  useEffect(() => {
    fetchBillingData();

    // Listen for organization user updates to refresh billing data
    const handleUserUpdate = (event: CustomEvent) => {
      console.log('[SubscriptionPage] Organization users updated, refreshing billing data', event.detail);
      fetchBillingData();
    };

    window.addEventListener('organization-users-updated', handleUserUpdate as EventListener);

    return () => {
      window.removeEventListener('organization-users-updated', handleUserUpdate as EventListener);
    };
  }, [fetchBillingData]);

  const handleTierSelect = async (tierId: string) => {
    if (!subscription || !user?.organizationId) return;
    
    setUpdatingSubscription(true);
    setError(null);
    
    try {
      const tier = PRICING_TIERS.find(t => t.id === tierId);
      if (!tier) throw new Error('Invalid tier selected');
      
      await billingApi.updateSubscription(subscription.id, {
        tier: tier.tier,
        billingFrequency: billingFrequency,
      });
      
      await fetchBillingData();
    } catch (err) {
      console.error('Error updating subscription:', err);
      setError('Failed to update subscription. Please try again.');
    } finally {
      setUpdatingSubscription(false);
    }
  };

  const handleAddSpecializedProduct = async (productId: string, seatCount: number) => {
    if (!subscription) return;
    
    setUpdatingSubscription(true);
    setError(null);
    
    try {
      await billingApi.addSpecializedProduct(
        user?.organizationId || '',
        productId,
        seatCount
      );
      
      await fetchBillingData();
    } catch (err) {
      console.error('Error adding specialized product:', err);
      setError('Failed to add specialized product. Please try again.');
    } finally {
      setUpdatingSubscription(false);
    }
  };

  const handleUpdateSpecializedProduct = async (productId: string, seatCount: number) => {
    if (!subscription) return;
    
    setUpdatingSubscription(true);
    setError(null);
    
    try {
      await billingApi.updateSpecializedProduct(productId, seatCount);
      
      await fetchBillingData();
    } catch (err) {
      console.error('Error updating specialized product:', err);
      setError('Failed to update specialized product. Please try again.');
    } finally {
      setUpdatingSubscription(false);
    }
  };

  const handleRemoveSpecializedProduct = async (productId: string) => {
    if (!subscription) return;
    
    setUpdatingSubscription(true);
    setError(null);
    
    try {
      await billingApi.cancelSpecializedProduct(productId);
      await fetchBillingData();
    } catch (err) {
      console.error('Error removing specialized product:', err);
      setError('Failed to remove specialized product. Please try again.');
    } finally {
      setUpdatingSubscription(false);
    }
  };

  const handleManageUsers = (productId: string) => {
    navigate(`/billing/products/${productId}/users`);
  };

  const getCurrentTierInfo = (): PricingTier | undefined => {
    return PRICING_TIERS.find(t => t.tier === subscription?.planTier);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <Container maxWidth="xl" style={{ paddingTop: 32, paddingBottom: 32 }}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Subscription & Billing
      </Typography>

      {error && (
        <Alert severity="error" style={{ marginBottom: 24 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Current Subscription Overview */}
      {subscription && billingSummary && (
        <Card style={{ marginBottom: 32 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Current Subscription
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Plan
                </Typography>
                <Typography variant="h6">
                  {getCurrentTierInfo()?.name || subscription.planTier}
                </Typography>
                <Chip 
                  label={subscription.status} 
                  color={subscription.status === 'active' ? 'success' : 'warning'}
                  size="small"
                  style={{ marginTop: 8 }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Billing Cycle
                </Typography>
                <Typography variant="h6">
                  {subscription.billingFrequency}
                </Typography>
                <Typography variant="body2">
                  Next billing: {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Monthly Total
                </Typography>
                <Typography variant="h6">
                  ${subscription.totalMonthlyAmount}
                </Typography>
                <Typography variant="body2">
                  {subscription.userCount} user{subscription.userCount > 1 ? 's' : ''}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Agent Usage
                </Typography>
                <Typography variant="h6">
                  {billingSummary.currentUsage.customAgents} / {
                    subscription.includesUnlimitedAgents 
                      ? 'Unlimited' 
                      : subscription.customAgentLimit || 0
                  }
                </Typography>
                <Typography variant="body2">
                  Custom agents
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Paper style={{ marginBottom: 24 }}>
        <Tabs 
          value={activeTab} 
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Subscription Plans" icon={<CreditCardIcon />} iconPosition="start" />
          <Tab label="Specialized Products" icon={<SettingsIcon />} iconPosition="start" />
          <Tab label="Token Usage" icon={<TokenIcon />} iconPosition="start" />
          <Tab label="Invoices" icon={<ReceiptIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Subscription Plans Tab */}
      <TabPanel value={activeTab} index={0}>
        <div style={{ marginBottom: 32 }}>
          <Typography variant="h6" gutterBottom>
            Select Billing Frequency
          </Typography>
          <ToggleButtonGroup
            value={billingFrequency}
            exclusive
            onChange={(_, newFrequency) => newFrequency && setBillingFrequency(newFrequency)}
            style={{ marginBottom: 24 }}
          >
            <ToggleButton value={BillingFrequency.MONTHLY}>
              Monthly
            </ToggleButton>
            <ToggleButton value={BillingFrequency.QUARTERLY}>
              Quarterly (5% off)
            </ToggleButton>
            <ToggleButton value={BillingFrequency.YEARLY}>
              Yearly (10% off)
            </ToggleButton>
          </ToggleButtonGroup>
        </div>

        <Grid container spacing={3}>
          {PRICING_TIERS.map((tier) => (
            <Grid item xs={12} md={tier.id === 'mep' ? 12 : 4} key={tier.id}>
              <TierSelectionCard
                tier={tier}
                currentTier={subscription?.planTier.toLowerCase()}
                billingFrequency={billingFrequency}
                onSelectTier={handleTierSelect}
                isLoading={updatingSubscription}
              />
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      {/* Specialized Products Tab */}
      <TabPanel value={activeTab} index={1}>
        <Typography variant="h6" gutterBottom>
          Specialized Product Add-ons
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Enhance your subscription with specialized tools and agents designed for specific industries and use cases.
        </Typography>

        <Grid container spacing={3}>
          {specializedProducts.map((product) => {
            const productSubscription = subscription?.specializedProducts?.find(
              sp => sp.productId === product.id
            );
            
            return (
              <Grid item xs={12} md={6} key={product.id}>
                <SpecializedProductCard
                  product={product}
                  subscription={productSubscription}
                  onAddProduct={handleAddSpecializedProduct}
                  onUpdateSeats={handleUpdateSpecializedProduct}
                  onRemoveProduct={handleRemoveSpecializedProduct}
                  onManageUsers={handleManageUsers}
                  isLoading={updatingSubscription}
                  canAddProduct={!!subscription}
                />
              </Grid>
            );
          })}
        </Grid>
      </TabPanel>

      {/* Token Usage Tab */}
      <TabPanel value={activeTab} index={2}>
        <Button
          variant="contained"
          onClick={() => navigate('/billing/tokens')}
        >
          Manage Token Usage
        </Button>
      </TabPanel>

      {/* Invoices Tab */}
      <TabPanel value={activeTab} index={3}>
        <Button
          variant="contained"
          onClick={() => navigate('/billing/invoices')}
        >
          View Invoices
        </Button>
      </TabPanel>
    </Container>
  );
};